<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';

import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import CrmTaskAPI from 'dashboard/api/crmTasks';
import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');

const scope = ref('mine');
const scopeOptions = computed(() => [
  { value: 'mine', label: t('CRM_FUNIL.AGENDA.MINE') },
  { value: 'all', label: t('CRM_FUNIL.AGENDA.ALL') },
]);

const tasks = ref([]);
const isLoading = ref(false);
const completingId = ref(null);

const newTaskDialogRef = ref(null);
const isCreating = ref(false);
const showErrors = ref(false);
const contactOptions = ref([]);

const newTaskForm = reactive({
  contactId: '',
  contactName: '',
  title: '',
  kind: 'task',
  dueAt: '',
  assigneeId: '',
});

const assigneeOptions = computed(() => [
  { value: '', label: t('CRM_FUNIL.CARD.UNASSIGNED') },
  ...(agents.value || []).map(agent => ({
    value: agent.id,
    label: agent.name,
  })),
]);

// Same kind list as `CrmTasksDialog` — duplicated for the same reason as
// `camelizeTask` below (no shared composable for this small CRM slice yet).
const kindOptions = computed(() => [
  { value: 'task', label: t('CRM_FUNIL.KIND.TASK') },
  { value: 'meeting', label: t('CRM_FUNIL.KIND.MEETING') },
  { value: 'call', label: t('CRM_FUNIL.KIND.CALL') },
  { value: 'other', label: t('CRM_FUNIL.KIND.OTHER') },
]);
const KIND_LABEL_KEYS = {
  task: 'TASK',
  meeting: 'MEETING',
  call: 'CALL',
  other: 'OTHER',
};
const kindLabel = kind =>
  t(`CRM_FUNIL.KIND.${KIND_LABEL_KEYS[kind] || 'TASK'}`);

// Same manual snake_case -> camelCase step as `CrmTasksDialog` (tasks have
// no Pinia store of their own).
const camelizeTask = raw => ({
  id: raw.id,
  title: raw.title,
  kind: raw.kind,
  dueAt: raw.due_at,
  status: raw.status,
  completedAt: raw.completed_at,
  dealId: raw.deal_id,
  contactId: raw.contact_id,
  assigneeId: raw.assignee_id,
  assignee: raw.assignee,
  contact: raw.contact,
  deal: raw.deal,
});

const loadTasks = async () => {
  isLoading.value = true;
  try {
    const params = { status: 'pending' };
    if (scope.value === 'mine' && currentUser.value?.id) {
      params.assignee_id = currentUser.value.id;
    }
    const { data } = await CrmTaskAPI.get(params);
    tasks.value = (data?.payload || []).map(camelizeTask);
  } catch {
    tasks.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Local-day comparisons (same TZ-safe recipe as CrmCard/CrmReport/
// CrmTasksDialog): `toLocaleDateString('en-CA')` reads a Date in the
// browser's local timezone as 'YYYY-MM-DD', so string compares never drift
// a day around midnight.
const toLocalDay = iso => new Date(iso).toLocaleDateString('en-CA');
const todayStr = () => new Date().toLocaleDateString('en-CA');
const tomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString('en-CA');
};

const sections = computed(() => {
  const today = todayStr();
  const tomorrow = tomorrowStr();
  const buckets = {
    overdue: [],
    today: [],
    tomorrow: [],
    later: [],
    noDate: [],
  };

  tasks.value.forEach(task => {
    if (!task.dueAt) {
      buckets.noDate.push(task);
      return;
    }
    const day = toLocalDay(task.dueAt);
    if (day < today) buckets.overdue.push(task);
    else if (day === today) buckets.today.push(task);
    else if (day === tomorrow) buckets.tomorrow.push(task);
    else buckets.later.push(task);
  });

  return [
    {
      key: 'overdue',
      label: t('CRM_FUNIL.AGENDA.OVERDUE'),
      items: buckets.overdue,
      highlight: true,
    },
    { key: 'today', label: t('CRM_FUNIL.AGENDA.TODAY'), items: buckets.today },
    {
      key: 'tomorrow',
      label: t('CRM_FUNIL.AGENDA.TOMORROW'),
      items: buckets.tomorrow,
    },
    { key: 'later', label: t('CRM_FUNIL.AGENDA.LATER'), items: buckets.later },
    {
      key: 'noDate',
      label: t('CRM_FUNIL.AGENDA.NO_DATE'),
      items: buckets.noDate,
    },
  ].filter(section => section.items.length);
});

const formatTime = iso =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const handleComplete = async task => {
  if (completingId.value) return;

  completingId.value = task.id;
  try {
    await CrmTaskAPI.update(task.id, { status: 'done' });
    await loadTasks();
  } catch {
    useAlert(t('CRM_FUNIL.AGENDA.ERROR'));
  } finally {
    completingId.value = null;
  }
};

const toContactOption = contact => ({ label: contact.name, value: contact.id });

const fetchContacts = async query => {
  try {
    const {
      data: { payload },
    } = await ContactAPI.search(query, 1);
    contactOptions.value = (payload || []).map(toContactOption);
  } catch {
    contactOptions.value = [];
  }
};

const handleOpenContactCombobox = () => fetchContacts('');
const handleSearchContact = useDebounceFn(query => {
  fetchContacts(query?.trim() || '');
}, 300);

const handleSelectContact = value => {
  const id = value ? Number(value) : '';
  const selected = contactOptions.value.find(option => option.value === id);
  newTaskForm.contactId = id;
  newTaskForm.contactName = selected?.label || '';
};

// Contact is optional here (standalone agenda items) — the combobox no
// longer has a required-field guard, unlike the deal-creation one.
const resetNewTaskForm = () => {
  newTaskForm.contactId = '';
  newTaskForm.contactName = '';
  newTaskForm.title = '';
  newTaskForm.kind = 'task';
  newTaskForm.dueAt = '';
  newTaskForm.assigneeId = currentUser.value?.id || '';
  contactOptions.value = [];
  showErrors.value = false;
};

const openNewTaskDialog = () => {
  resetNewTaskForm();
  newTaskDialogRef.value?.open();
};

const closeNewTaskDialog = () => newTaskDialogRef.value?.close();

const handleCreateTask = async () => {
  if (isCreating.value) return;

  showErrors.value = true;
  if (!newTaskForm.title.trim()) return;

  isCreating.value = true;
  try {
    await CrmTaskAPI.create({
      title: newTaskForm.title.trim(),
      kind: newTaskForm.kind,
      due_at: newTaskForm.dueAt
        ? new Date(newTaskForm.dueAt).toISOString()
        : undefined,
      ...(newTaskForm.contactId ? { contact_id: newTaskForm.contactId } : {}),
      assignee_id: newTaskForm.assigneeId || undefined,
    });
    closeNewTaskDialog();
    await loadTasks();
  } catch {
    useAlert(t('CRM_FUNIL.AGENDA.ERROR'));
  } finally {
    isCreating.value = false;
  }
};

const backToPipeline = () =>
  router.push({
    name: 'crm_index',
    params: { accountId: route.params.accountId },
  });

onMounted(() => {
  if (!agents.value?.length) store.dispatch('agents/get');
  loadTasks();
});

watch(scope, loadTasks);

defineExpose({
  scope,
  tasks,
  sections,
  loadTasks,
  handleComplete,
  newTaskForm,
  handleCreateTask,
  handleSelectContact,
  openNewTaskDialog,
});
</script>

<template>
  <div class="flex flex-col w-full h-full p-4 gap-4 overflow-y-auto">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <Button
          icon="i-lucide-arrow-left"
          variant="ghost"
          color="slate"
          size="sm"
          :label="t('CRM_FUNIL.AGENDA.BACK')"
          @click="backToPipeline"
        />
        <h1 class="text-lg font-medium text-n-slate-12">
          {{ t('CRM_FUNIL.AGENDA.TITLE') }}
        </h1>
      </div>
      <div class="flex items-center gap-2">
        <Select
          v-model="scope"
          :options="scopeOptions"
          :aria-label="t('CRM_FUNIL.AGENDA.TITLE')"
        />
        <Button
          :label="t('CRM_FUNIL.AGENDA.NEW')"
          icon="i-lucide-plus"
          size="sm"
          @click="openNewTaskDialog"
        />
      </div>
    </div>

    <p v-if="!isLoading && !tasks.length" class="text-sm text-n-slate-11">
      {{ t('CRM_FUNIL.AGENDA.EMPTY') }}
    </p>

    <div
      v-for="section in sections"
      :key="section.key"
      class="flex flex-col gap-2"
    >
      <h2
        class="text-sm font-medium"
        :class="section.highlight ? 'text-n-ruby-9' : 'text-n-slate-12'"
      >
        {{ section.label }}
      </h2>
      <div
        v-for="task in section.items"
        :key="task.id"
        class="flex items-center justify-between gap-2 p-2 border rounded-lg"
        :class="section.highlight ? 'border-n-ruby-6' : 'border-n-weak'"
      >
        <div class="flex flex-col min-w-0 gap-0.5">
          <div class="flex items-center gap-2 text-xs">
            <span v-if="task.dueAt" class="font-medium text-n-slate-12">
              {{ formatTime(task.dueAt) }}
            </span>
            <span
              class="px-2 py-0.5 text-xs rounded-full whitespace-nowrap bg-n-slate-3 text-n-slate-11"
            >
              {{ kindLabel(task.kind) }}
            </span>
            <span class="text-n-slate-12">{{ task.title }}</span>
          </div>
          <span
            v-if="task.contact?.name || task.deal?.title"
            class="text-xs text-n-slate-10 truncate"
          >
            {{ task.contact?.name || task.deal?.title }}
          </span>
          <span v-if="task.assignee?.name" class="text-xs text-n-slate-10">
            {{ task.assignee.name }}
          </span>
        </div>
        <Button
          :label="t('CRM_FUNIL.TASKS.COMPLETE')"
          variant="faded"
          color="slate"
          size="sm"
          type="button"
          :disabled="completingId === task.id"
          @click="handleComplete(task)"
        />
      </div>
    </div>

    <Dialog
      ref="newTaskDialogRef"
      width="lg"
      overflow-y-auto
      :confirm-button-label="t('CRM_FUNIL.NEW_DEAL.SUBMIT')"
      :cancel-button-label="t('CRM_FUNIL.NEW_DEAL.CANCEL')"
      :is-loading="isCreating"
      :disable-confirm-button="isCreating"
      @confirm="handleCreateTask"
      @close="resetNewTaskForm"
    >
      <div class="flex flex-col gap-4">
        <span class="py-1 text-sm font-medium text-n-slate-12">
          {{ t('CRM_FUNIL.AGENDA.NEW') }}
        </span>

        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CRM_FUNIL.KIND.LABEL') }}
          </label>
          <Select v-model="newTaskForm.kind" :options="kindOptions" />
        </div>

        <Input
          v-model="newTaskForm.title"
          :label="t('CRM_FUNIL.TASKS.TITLE_FIELD')"
          :disabled="isCreating"
        />
        <Input
          v-model="newTaskForm.dueAt"
          type="datetime-local"
          :label="t('CRM_FUNIL.TASKS.DUE')"
          :disabled="isCreating"
        />
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CRM_FUNIL.TASKS.ASSIGNEE') }}
          </label>
          <Select v-model="newTaskForm.assigneeId" :options="assigneeOptions" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CRM_FUNIL.AGENDA.CONTACT') }}
          </label>
          <ComboBox
            :model-value="newTaskForm.contactId"
            :options="contactOptions"
            :display-label="newTaskForm.contactName"
            :placeholder="t('CRM_FUNIL.NEW_DEAL.CONTACT_PLACEHOLDER')"
            :search-placeholder="t('CRM_FUNIL.NEW_DEAL.CONTACT_PLACEHOLDER')"
            use-api-results
            @open="handleOpenContactCombobox"
            @search="handleSearchContact"
            @update:model-value="handleSelectContact"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>
